import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { APIS, URLS, SF_SURV_PRTCPNT_LIST_SRCH, SG_SURV_PRTCPNT_LIST } from "./services/PtliSurvPrtcpntService";
import { SurvPrtcpntPpupInt } from "@/comn/comn/surv/SurvPrtcpntPpupInt"; // TODO: Component not implemented yet

/*
 * Survey Participate List
 * !== 설문 참여 목록 ==!
 */
export const PTLI0305001Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0019"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0024" }, // 업무구분
            { area: "comnCd", comnCd: "COM0052" }, // 포털유형
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survPrtcpntListSrch: useForm({
            defaultSchema: SF_SURV_PRTCPNT_LIST_SRCH,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        survPrtcpntList: useGrid({
            defaultSchema: SG_SURV_PRTCPNT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */

    const readSessionUser = () => {
        try {
            const raw = sessionStorage.getItem("user-info") || localStorage.getItem("user-info");
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    };

    const readSessionLangCd = () => {
        try {
            const raw = sessionStorage.getItem("menu-config") || localStorage.getItem("menu-config");
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    };

    const fetch = {
        getSurvPrtcpntList: useFetch({
            api: (page = grid.survPrtcpntList.page) => {
                const cond = form.survPrtcpntListSrch.getValues();
                const usr: any = readSessionUser();
                const payload = {
                    ...cond,
                    usrDmnId: usr?.usrId ?? usr?.asisSysmUsrId ?? "",
                };
                return APIS.getSurvPrtcpntList(payload, page, grid.survPrtcpntList.size);
            },
            enabled: comnUtils.isEmpty(form.survPrtcpntListSrch.errors) && form.survPrtcpntListSrch.isSubmitted,
            key: [grid.survPrtcpntList.page, grid.survPrtcpntList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.survPrtcpntListSrch.getValues(),
                    page: grid.survPrtcpntList.page,
                    size: grid.survPrtcpntList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
    };

    const handler = {
        onSrchBtnClick: () => {
            grid.survPrtcpntList.setPage(0);
            fetch.getSurvPrtcpntList.fetch(0);
        },
        openSurvPpup: (qestnrId: string) => {
            const langCd = readSessionLangCd();
            modal.openModal({
                size: "lg",
                draggable: true,
                close: false,
                topClose: false,
                content: (
                    <SurvPrtcpntPpupInt
                        qestnrId={qestnrId}
                        langCd={langCd}
                        // 설문 저장/제출 후: 모달 닫고 리스트 재조회
                        onAfterSubmit={() => {
                            modal.closeModal();
                            fetch.getSurvPrtcpntList.fetch(grid.survPrtcpntList.page);
                        }}
                    />
                ),
            });
        },
    };

    const render = {
        cell: {
            qustnrTtle: ({ rowValues, value }: any) => (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        if (rowValues.qustnrPrtcpntYn === "1") {
                            modal.openModal({
                                content: "msg.ptl.00840", // You have already participated in this survey.  !== 이미 참가한 설문입니다. ==!
                            });
                        } else {
                            handler.openSurvPpup(rowValues.qestnrId);
                        }
                    }}
                >
                    {value}
                </a>
            ),
        },
    };
    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.survPrtcpntListSrch.schema.bsopClsfCd} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button
                                role={"reset"}
                                onClick={() => {
                                    form.survPrtcpntListSrch.reset();
                                }}
                            />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.survPrtcpntList.grid}
                            data={fetch.getSurvPrtcpntList.data?.survPrtcpntList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
